import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './landing.css';
import { API_BASE } from './config/api';
import { LinkedinOutlined, XOutlined, YoutubeOutlined, InstagramOutlined } from '@ant-design/icons';
import useCurrencyDisplay from './utils/useCurrencyDisplay';


const HERO_STATS = [
  { value: '+38%', label: 'Conversion lift after 60 days' },
  { value: '2.1M', label: 'Sessions analyzed monthly' },
  { value: '< 5min', label: 'Average setup time' }
];

const PREVIEW_ROWS = [
  { label: 'Homepage', value: 86, color: '#60a5fa' },
  { label: 'Pricing', value: 73, color: '#c084fc' },
  { label: 'Checkout', value: 58, color: '#f472b6' }
];

const PREVIEW_CARDS = [
  {
    title: 'Live experience score',
    score: '92 / 100',
    badge: 'Realtime',
    rows: PREVIEW_ROWS,
    insight: 'Users hesitate on pricing. Add social proof near CTA.'
  },
  {
    title: 'Session recordings',
    score: 'Coming Soon',
    badge: 'Coming Soon',
    rows: [
      { label: 'Mobile', value: 62, color: '#10b981' },
      { label: 'Desktop', value: 38, color: '#3b82f6' },
      { label: 'Tablet', value: 12, color: '#f59e0b' }
    ],
    insight: 'Coming soon: Watch real user journeys to understand exactly how visitors interact with your website.'
  },
  {
    title: 'Conversion funnel',
    score: '18.2%',
    badge: 'This week',
    rows: [
      { label: 'Landing', value: 100, color: '#6366f1' },
      { label: 'Product', value: 45, color: '#8b5cf6' },
      { label: 'Checkout', value: 18, color: '#ec4899' }
    ],
    insight: 'Checkout drop-off detected. Test trust badges.'
  },
  {
    title: 'AI insights generated',
    score: '34',
    badge: 'Today',
    rows: [
      { label: 'Top pages analyzed', value: 12, color: '#6366f1' },
      { label: 'Patterns found', value: 8, color: '#8b5cf6' },
      { label: 'Recommendations', value: 14, color: '#22c55e' }
    ],
    insight: 'Homepage has 2.3K views. AI suggests optimizing CTA placement.'
  }
];

const ANALYTICS_CARDS = [
  {
    title: 'Form Analytics',
    metric: '72% friction score',
    detail: 'Phone number field causing abandonment. AI suggests simplification.',
    gradient: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(248,250,252,0.9))'
  },
  {
    title: 'Session Recordings',
    metric: 'Coming Soon',
    detail: 'Watch real user journeys to understand exactly how visitors interact with your website.',
    gradient: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(248,250,252,0.9))'
  },
  {
    title: 'AI Insights',
    metric: 'Top pages analyzed',
    detail: 'Homepage: 2.3K views, 312 clicks. AI recommends CTA optimization.',
    gradient: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(248,250,252,0.9))'
  }
];

const METRIC_CARDS = [
  { label: 'Websites monitored', value: '1,200+', detail: 'From scale-ups to marketplaces' },
  { label: 'AI insights shipped', value: '84K', detail: 'Actionable recommendations per month' },
  { label: 'Avg. resolution time', value: '3m 20s', detail: 'From alert to clarity' }
];

const FEATURE_LIST = [
  { 
    title: 'Heatmaps', 
    desc: 'Scroll heatmaps overlayed on your actual website pages - see exactly where users engage, click, and drop off.',
    longDesc: 'Our heatmap technology overlays engagement data directly onto your website, showing click patterns, scroll depth, and attention zones. Visualize user Behaviour on real page layouts to identify what works and what needs improvement.',
    badge: 'Popular',
    detailedTitle: 'Heatmap Analytics - Visual Engagement Tracking',
    detailedDescription: 'See exactly where users click, scroll, and engage on your website with our advanced heatmap technology. Our visual overlays show engagement patterns directly on your actual website pages, making it easy to identify what works and what doesn\'t. Track click patterns, scroll depth, rage clicks, and attention zones to optimize your site for maximum engagement and conversions.',
    features: [
      'Click heatmaps - Visualize where users click most frequently on each page',
      'Scroll depth tracking - See how far users scroll and where they stop',
      'Rage click detection - Automatically identify frustration points and broken elements',
      'Move maps - Track cursor movement patterns to understand user attention',
      'Attention maps - Measure time spent on each element and section',
      'Device-specific views - Separate heatmaps for mobile, tablet, and desktop users'
    ],
    benefits: 'Optimize page layouts based on real user Behaviour, identify high-performing content areas, reduce friction points that cause user frustration, improve conversion rates by understanding what captures attention, and make data-driven design decisions instead of guessing.'
  },
  { 
    title: 'Live Experience Score', 
    desc: 'Real-time score (0-100) showing your website\'s overall user experience health - updated every 5 seconds via API.',
    longDesc: 'Your Live Experience Score aggregates page speed, Core Web Vitals, error rates, and user satisfaction into one simple metric. Monitor it in real-time on your dashboard to catch issues instantly and validate improvements immediately.',
    badge: 'Real-time',
    detailedTitle: 'Live Experience Score - Real-Time UX Health Monitoring',
    detailedDescription: 'Monitor your website\'s overall user experience health with a single, real-time score from 0-100. Our Live Experience Score aggregates multiple critical metrics including page speed, Core Web Vitals, error rates, and user satisfaction into one easy-to-understand number. Updated every 5 seconds via API integration, you\'ll always know the current state of your site\'s user experience.',
    features: [
      'Real-time API integration - Direct connection to your site for instant updates',
      'Automatic score calculation - AI weighs performance, errors, and satisfaction',
      'Live updates every 5 seconds - No page refresh needed to see changes',
      'Core Web Vitals tracking - Monitor LCP, FID, CLS, and other Google metrics',
      'Error rate monitoring - Instant alerts when issues are detected',
      'Historical comparison - Track score trends over time and compare periods'
    ],
    benefits: 'Catch performance issues before they impact users, validate improvements immediately after deployment, monitor launches and major changes in real-time, get instant alerts when user experience degrades, and make informed decisions based on a comprehensive UX health metric.'
  },
  { 
    title: 'AI Copilot', 
    desc: 'Get instant insights and recommendations powered by AI analysis of user Behaviour.',
    longDesc: 'AI analyzes thousands of sessions and surfaces actionable insights. Know exactly what to fix and why.',
    badge: 'New',
    detailedTitle: 'AI Copilot - Intelligent Insights & Recommendations',
    detailedDescription: 'Let AI do the heavy lifting of analyzing user Behaviour and surfacing actionable insights. Our AI Copilot continuously analyzes thousands of sessions, heatmaps, and user interactions to identify patterns, detect issues, and recommend specific improvements. Get clear, prioritized recommendations on exactly what to fix and why it matters.',
    features: [
      'Automated issue detection - AI identifies problems without manual analysis',
      'Prioritized recommendations - Focus on high-impact improvements first',
      'Root cause analysis - Understand why issues occur, not just what they are',
      'A/B test suggestions - Get AI-powered ideas for experiments to run',
      'Trend identification - Spot patterns across thousands of user sessions',
      'Plain English explanations - No technical jargon, just clear insights'
    ],
    benefits: 'Save hours of manual analysis time, focus on improvements that actually matter, get expert-level insights without hiring analysts, discover issues you would have missed, validate hypotheses with AI-powered data analysis, and make confident decisions backed by intelligent recommendations.'
  },
  { 
    title: 'Session Recordings', 
    desc: 'Watch pixel-perfect replays of user sessions with click tracking, scroll Behaviour, and rage click detection.',
    longDesc: 'Replay actual user sessions with full playback controls, showing every click, scroll, and interaction. See device type, conversion status, and rage clicks. Understand exactly how users navigate, where they hesitate, and why they convert or drop off.',
    badge: 'Coming Soon',
    detailedTitle: 'Session Recordings - Watch Real User Behaviour',
    detailedDescription: 'Watch pixel-perfect replays of real user sessions to understand exactly how visitors interact with your website. Our session recordings capture every click, scroll, form interaction, and page navigation, giving you complete visibility into the user experience. See what users see, identify friction points, and discover why users convert or abandon.',
    features: [
      'Pixel-perfect playback - Watch sessions exactly as users experienced them',
      'Full playback controls - Play, pause, skip, and adjust speed as needed',
      'Click and scroll tracking - See every interaction in chronological order',
      'Rage click detection - Automatically flag sessions with user frustration',
      'Device and browser info - Know what device, browser, and screen size users have',
      'Conversion status - Filter by converted vs. abandoned sessions'
    ],
    benefits: 'Understand why users drop off at specific points, identify usability issues that analytics alone can\'t reveal, validate design changes by watching real user Behaviour, discover bugs and broken elements users encounter, and improve customer support by seeing exactly what users experienced.'
  },
  { 
    title: 'Conversion Funnels', 
    desc: 'Track user journeys through your conversion paths and identify where they drop off.',
    longDesc: 'Visualize complete user journeys from landing to conversion. Pinpoint exactly where you lose customers.',
    badge: null,
    detailedTitle: 'Conversion Funnels - Optimize Your User Journey',
    detailedDescription: 'Visualize and optimize every step of your conversion funnel. Track user journeys from landing page to final conversion, identify exactly where users drop off, and understand why. Our funnel analytics show you conversion rates at each stage, time spent per step, and common exit points so you can focus optimization efforts where they\'ll have the biggest impact.',
    features: [
      'Multi-step funnel tracking - Monitor conversions through any number of steps',
      'Drop-off analysis - See exactly where and why users abandon the journey',
      'Conversion rate by step - Know which stages perform well and which need work',
      'Time-to-convert tracking - Understand how long successful conversions take',
      'Segment comparison - Compare funnels by traffic source, device, or user type',
      'Custom funnel creation - Build funnels for any conversion goal'
    ],
    benefits: 'Identify the biggest bottlenecks in your conversion path, prioritize optimization efforts based on data, increase conversion rates by fixing critical drop-off points, understand the complete customer journey, reduce cart abandonment and form drop-offs, and validate that funnel improvements actually work.'
  },
  { 
    title: 'Form Analytics', 
    desc: 'See which form fields cause friction and where users abandon.',
    longDesc: 'Track field-by-field interactions, time spent, and abandonment rates to optimize your forms.',
    badge: null,
    detailedTitle: 'Form Analytics - Optimize Every Field',
    detailedDescription: 'Understand exactly how users interact with your forms, field by field. Our form analytics track every interaction, showing you which fields cause hesitation, which ones users skip, and where they abandon. See completion rates, time spent per field, error rates, and more to optimize your forms for maximum conversions.',
    features: [
      'Field-by-field tracking - Monitor interactions with every form element',
      'Abandonment analysis - See exactly where users give up on forms',
      'Time-to-complete metrics - Identify fields that take too long to fill',
      'Error tracking - Know which fields cause validation errors most often',
      'Refill detection - See when users go back to change previous answers',
      'Completion rates - Track form success rates and drop-off percentages'
    ],
    benefits: 'Reduce form abandonment by fixing problematic fields, improve conversion rates on signup and checkout forms, identify confusing or unnecessary fields to remove, optimize field order based on user Behaviour, reduce errors with better validation and hints, and create frictionless form experiences that users actually complete.'
  }
];

const INTEGRATIONS = [
  { name: 'Shopify', category: 'E-commerce', color: '#96bf48' },
  { name: 'WordPress', category: 'CMS', color: '#21759b' },
  { name: 'Stripe', category: 'Payment', color: '#635bff' },
  { name: 'Google Analytics', category: 'Analytics', color: '#e37400' },
  { name: 'Zapier', category: 'Automation', color: '#ff4a00' },
  { name: 'Salesforce', category: 'CRM', color: '#00a1e0' }
];

const PRIVACY_FEATURES = [
  { 
    title: 'GDPR- & CCPA-ready', 
    description: 'Built-in compliance with global privacy regulations. Automatic data handling and user rights management.',
    icon: '🔒',
    buttonText: 'Learn More'
  },
  { 
    title: 'User-controlled data retention', 
    description: 'Let users decide how long their data is stored. Flexible retention policies that respect user preferences.',
    icon: '⏱️',
    buttonText: 'Configure'
  },
  { 
    title: 'Anonymous mode (just stats)', 
    description: 'Collect insights without personal data. Get the analytics you need while protecting user privacy.',
    icon: '👤',
    buttonText: 'Enable'
  },
  { 
    title: 'Mask or Block Sensitive Info', 
    description: 'Automatically detect and hide sensitive information like passwords, credit cards, and personal data.',
    icon: '🛡️',
    buttonText: 'Set Rules'
  }
];

const COMPANY_SIZE_OPTIONS = [
  '',
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1000+',
];

const INDUSTRY_OPTIONS = [
  '',
  'SaaS / Software',
  'E-commerce / Retail',
  'Agency / Services',
  'Marketplace / Platform',
  'Fintech',
  'Education',
  'Media / Publishing',
  'Other',
];

const PRICING_PLANS = [
  {
    planId: 1, // Assuming Starter Plan has ID 1 in database
    name: 'Starter',
    price: '£9.99/month',
    annualPrice: '£101.90/year',
    monthlyPrice: 9.99,
    annualPriceValue: 101.90,
    stripePriceId: 'price_1SVUtoPl4m7YNcZrgC54kfDj',
    note: 'Great for a single site just getting started',
    ctaLabel: 'Sign Up',
    features: [
      '1 website',
      '10,000 events / month',
      'Heatmaps: Basic (Pageviews, Clicks)',
      'Analytics: Basic',
      '1 user (owner)',
      '7 days data retention',
      'Support: Email'
    ]
  },
  {
    planId: 2, // Assuming Growth Plan has ID 2 in database
    name: 'Growth',
    price: '£29.99/month',
    annualPrice: '£325.90/year',
    monthlyPrice: 29.99,
    annualPriceValue: 325.90,
    stripePriceId: 'price_1SVUvwPl4m7YNcZrmzttZIMD',
    note: 'For growing teams managing multiple sites',
    ctaLabel: 'Sign Up',
    features: [
      '3 websites',
      '100,000 events / month',
      'Heatmaps: Unlimited (Pageviews, Scrolls, Clicks, Form Submissions, Downloads, JavaScript Errors)',
      'Analytics: Advanced',
      'AI Agent: Actionable Suggestions',
      'Snippet Management: Advanced with Multiple Platform Integrations',
      'Up to 3 users',
      '6 months data retention',
      'Support: Priority Email'
    ]
  },
  {
    planId: 3, // Assuming Enterprise Plan has ID 3 in database
    name: 'Enterprise',
    price: '£999.99/month',
    annualPrice: '£1019.90/year',
    monthlyPrice: 999.99,
    annualPriceValue: 1019.90,
    stripePriceId: 'price_1SVUwwPl4m7YNcZr627JXgIn',
    note: 'For larger organizations with advanced needs',
    ctaLabel: 'Sign Up',
    features: [
      '10 websites',
      '1,000,000+ events / month',
      'Heatmaps: Unlimited (Pageviews, Scrolls, Clicks, Form Submissions, Downloads, JavaScript Errors)',
      'Analytics: Advanced',
      'AI Agent: Actionable Suggestions',
      'Snippet Management: Advanced with Multiple Platform Integrations',
      'Up to 10 users & roles',
      '12 months data retention',
      'Support: Priority Email'
    ]
  },
  {
    name: 'White Label',
    price: 'Contact Sales',
    note: 'Run Asuma as your own analytics product',
    ctaLabel: 'Contact Sales',
    features: [
      'Custom branding & domain',
      'Unlimited websites & events',
      'All Enterprise features',
      'Dedicated account manager',
      'Custom integrations',
      'SLA guarantee'
    ]
  }
];

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

function Header() {
  const navigate = useNavigate();
  const [showContactModal, setShowContactModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactError, setContactError] = useState('');
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactForm, setContactForm] = useState({
    company_name: '',
    contact_name: '',
    business_email: '',
    phone: '',
    website: '',
    company_size: '',
    industry: '',
    message: '',
  });
  const [industryOption, setIndustryOption] = useState('');
  const [industryOther, setIndustryOther] = useState('');

  const openContactModal = React.useCallback(() => {
    setShowContactModal(true);
    setContactError('');
    setContactSuccess(false);
  }, []);

  React.useEffect(() => {
    const handler = () => openContactModal();
    window.addEventListener('open-contact-modal', handler);
    return () => window.removeEventListener('open-contact-modal', handler);
  }, [openContactModal]);

  const closeContactModal = () => {
    if (contactSubmitting) return;
    setShowContactModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleIndustryChange = (e) => {
    const value = e.target.value;
    setIndustryOption(value);
    if (value === 'Other') {
      setIndustryOther('');
      setContactForm((prev) => ({ ...prev, industry: '' }));
    } else {
      setIndustryOther('');
      setContactForm((prev) => ({ ...prev, industry: value }));
    }
  };

  const handleIndustryOtherChange = (e) => {
    const value = e.target.value;
    setIndustryOther(value);
    setContactForm((prev) => ({ ...prev, industry: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.company_name || !contactForm.business_email) {
      setContactError('Please provide your company name and business email.');
      return;
    }
    setContactSubmitting(true);
    setContactError('');
    setContactSuccess(false);
    try {
      const rawWebsite = (contactForm.website || '').trim();
      const normalizedWebsite = rawWebsite
        ? `https://${rawWebsite.replace(/^https?:\/\//i, '')}`
        : '';
      const res = await fetch(`${API_BASE}/sales/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...contactForm,
          website: normalizedWebsite,
          plan: 'White Label',
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error((data && data.error) || 'Failed to send enquiry');
      }
      setContactSuccess(true);
      setContactForm({
        company_name: '',
        contact_name: '',
        business_email: '',
        phone: '',
        website: '',
        company_size: '',
        industry: '',
        message: '',
      });
      setIndustryOption('');
      setIndustryOther('');
    } catch (err) {
      setContactError(err.message || 'Failed to send enquiry');
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <>
      <header className="hdr container">
        <div className="brand" onClick={() => navigate('/')} style={{ cursor: 'pointer', fontSize: '24px', fontWeight: '700', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src="/headerlogo.png" alt="Asuma Logo" style={{ height: '32px', width: 'auto' }} />
            <span className="logo-text">Asuma</span>
          </div>
          <div className="logo-tagline" style={{ fontSize: '12px', fontWeight: '400', marginTop: '-2px', fontStyle: 'italic' }}>Customer intelligence for growing businesses.</div>
        </div>
        <nav className="nav">
          <button type="button" onClick={() => scrollToId('features')}>Features</button>
          <button type="button" onClick={() => scrollToId('pricing')}>Pricing</button>
          <button type="button" onClick={openContactModal}>White Label</button>
        </nav>
        <div className="hdr-actions">
          <button className="btn btn-ghost" type="button" onClick={openContactModal}>Contact Sales</button>
          <button className="btn btn-ghost" type="button" onClick={() => navigate('/login')}>Log in</button>
          <button className="btn btn-primary hdr-cta" type="button" onClick={() => navigate('/signup')}>Start Free Trial</button>
        </div>
        <button 
          className="mobile-menu-btn" 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {mobileMenuOpen ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </>
            ) : (
              <>
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </>
            )}
          </svg>
        </button>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu">
          <button type="button" onClick={() => { scrollToId('features'); setMobileMenuOpen(false); }}>Features</button>
          <button type="button" onClick={() => { scrollToId('pricing'); setMobileMenuOpen(false); }}>Pricing</button>
          <button type="button" onClick={() => { openContactModal(); setMobileMenuOpen(false); }}>White Label</button>
          <button type="button" onClick={() => { openContactModal(); setMobileMenuOpen(false); }}>Contact Sales</button>
          <button type="button" onClick={() => navigate('/login')}>Log in</button>
          <button className="btn btn-primary" type="button" onClick={() => navigate('/signup')}>Start Free Trial</button>
        </div>
      )}

      {/* Contact Sales Modal */}
      {showContactModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            overflowY: 'auto',
            padding: '24px 0',
          }}
          onClick={closeContactModal}
        >
          <div
            style={{
              maxWidth: 520,
              width: '100%',
              margin: '0 16px',
              background: '#020617',
              borderRadius: 16,
              padding: 24,
              boxShadow: '0 24px 70px rgba(15,23,42,0.9)',
              border: '1px solid rgba(148,163,184,0.4)',
              color: '#e5e7eb',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <p className="eyebrow">Book a Demo</p>
                <h3 style={{ margin: 0 }}>Talk to sales</h3>
              </div>
              <button
                type="button"
                onClick={closeContactModal}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  fontSize: 18,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            <p style={{ marginBottom: 16, fontSize: 14, color: '#9ca3af' }}>
              Share a few details about your business and we will reach out with demo scheduling and pricing options.
            </p>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Company name*</label>
                  <input
                    type="text"
                    name="company_name"
                    value={contactForm.company_name}
                    onChange={handleChange}
                    required
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Contact name</label>
                  <input
                    type="text"
                    name="contact_name"
                    value={contactForm.contact_name}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Business email*</label>
                  <input
                    type="email"
                    name="business_email"
                    value={contactForm.business_email}
                    onChange={handleChange}
                    required
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={contactForm.phone}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Website</label>
                  <input
                    type="text"
                    name="website"
                    value={contactForm.website}
                    onChange={handleChange}
                    placeholder="example.com"
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Company size</label>
                  <select
                    name="company_size"
                    value={contactForm.company_size}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  >
                    <option value="">Select size</option>
                    <option value="1-10">1-10</option>
                    <option value="11-50">11-50</option>
                    <option value="51-200">51-200</option>
                    <option value="201-500">201-500</option>
                    <option value="501-1000">501-1000</option>
                    <option value="1000+">1000+</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Industry</label>
                  <select
                    value={industryOption}
                    onChange={handleIndustryChange}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  >
                    <option value="">Select industry</option>
                    <option value="Technology">Technology</option>
                    <option value="E-commerce">E-commerce</option>
                    <option value="Finance">Finance</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Education">Education</option>
                    <option value="Media">Media</option>
                    <option value="Travel">Travel</option>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                {industryOption === 'Other' && (
                  <div>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Please specify</label>
                    <input
                      type="text"
                      value={industryOther}
                      onChange={handleIndustryOtherChange}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                    />
                  </div>
                )}
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Message</label>
                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={handleChange}
                    rows={3}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb', resize: 'vertical' }}
                  />
                </div>
              </div>
              {contactError && (
                <div style={{ marginTop: 12, padding: 8, borderRadius: 6, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#ef4444', fontSize: 13 }}>
                  {contactError}
                </div>
              )}
              {contactSuccess ? (
                <div style={{ marginTop: 12, padding: 8, borderRadius: 6, background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.2)', color: '#22c55e', fontSize: 13 }}>
                  Thank you! We'll be in touch soon.
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button
                    type="button"
                    onClick={closeContactModal}
                    disabled={contactSubmitting}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      borderRadius: 8,
                      border: '1px solid #4b5563',
                      background: 'transparent',
                      color: '#e5e7eb',
                      cursor: contactSubmitting ? 'not-allowed' : 'pointer',
                      fontSize: 14,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={contactSubmitting}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      borderRadius: 8,
                      border: 'none',
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      color: '#ffffff',
                      cursor: contactSubmitting ? 'not-allowed' : 'pointer',
                      fontSize: 14,
                    }}
                  >
                    {contactSubmitting ? 'Sending...' : 'Send'}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function Hero() {
  const navigate = useNavigate();
  const [currentCardIndex, setCurrentCardIndex] = useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCardIndex((prev) => (prev + 1) % PREVIEW_CARDS.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const currentCard = PREVIEW_CARDS[currentCardIndex];

  return (
    <section className="hero container">
      <div className="hero-grid">
        <div className="hero-left">
          <span className="eyebrow">Customer intelligence suite</span>
          <h1 className="hero-title">
            Understand visitor Behaviour.<br />
            Turn insights into action.<br />
            Improve your site.
          </h1>
          <p className="hero-sub">
            Visualize Behaviour with cinematic heatmaps, replay high-impact sessions, and let AI copilots summarize
            exactly what to fix next.
          </p>
          <div className="cta">
            <button className="btn btn-primary" type="button" onClick={() => navigate('/signup')}>Start Free Trial</button>
            <button className="btn btn-ghost" type="button" onClick={() => window.dispatchEvent(new CustomEvent('open-contact-modal'))}>
              Book a Demo
            </button>
          </div>
          <div className="hero-stats">
            {HERO_STATS.map((stat) => (
              <div key={stat.label}>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="hero-right">
          <div className="hero-preview-card" style={{ position: 'relative' }}>
            <div className="preview-header">
              <div>
                <p className="muted">{currentCard.title}</p>
                <h3>{currentCard.score}</h3>
              </div>
              <span className="badge">{currentCard.badge}</span>
            </div>
            <div className="preview-heatmap">
              {currentCard.rows.map((row, index) => (
                <div className="heatmap-row" key={row.label}>
                  <span>{row.label}</span>
                  <div className="heatmap-bar">
                    <span 
                      key={`${currentCardIndex}-${index}`}
                      style={{ 
                        width: `${row.value}%`, 
                        background: `linear-gradient(90deg, ${row.color}, #ffffff33)`,
                        animation: 'barGrow 0.8s ease-out forwards',
                        animationDelay: `${index * 0.1}s`
                      }} 
                    />
                  </div>
                  <span className="heatmap-value">{row.value}%</span>
                </div>
              ))}
            </div>
            <div className="preview-footer">
              <p>AI insight</p>
              <span>{currentCard.insight}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
              {PREVIEW_CARDS.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentCardIndex(index)}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    border: 'none',
                    background: index === currentCardIndex ? '#6366f1' : '#cbd5e1',
                    cursor: 'pointer',
                    padding: 0,
                    transition: 'all 0.3s ease'
                  }}
                  aria-label={`Go to card ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="analytics-grid">
        {ANALYTICS_CARDS.map((card) => (
          <div className="analytics-card" key={card.title} style={{ backgroundImage: card.gradient }}>
            <div>
              <p className="muted">{card.title}</p>
              <h4>{card.metric}</h4>
            </div>
            <span>{card.detail}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

const HeatmapVisual = () => (
  <svg viewBox="0 0 320 200" className="visual-frame" role="img" aria-label="Heatmap preview">
    <rect width="320" height="200" fill="#f8fafc" rx="8"/>
    <rect x="10" y="10" width="300" height="180" fill="white" rx="4"/>
    
    <text x="160" y="28" textAnchor="middle" fill="#1e293b" fontSize="11" fontWeight="700">Engagement Heatmap</text>
    
    {[0,1,2,3,4].map((i) => (
      <line key={i} x1="30" x2="290" y1={45 + i*25} y2={45 + i*25} stroke="#e5e7eb" strokeWidth="1" />
    ))}
    {[0,1,2,3,4,5].map((i) => (
      <line key={i} x1={30 + i*52} x2={30 + i*52} y1="45" y2="145" stroke="#e5e7eb" strokeWidth="1" />
    ))}
    
    <defs>
      <linearGradient id="engagementGradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#ef4444" stopOpacity="0.3" />
        <stop offset="100%" stopColor="#ef4444" stopOpacity="0" />
      </linearGradient>
    </defs>
    
    <polyline
      fill="url(#engagementGradient)"
      stroke="#ef4444"
      strokeWidth="2.5"
      points="30,95 82,75 134,85 186,65 238,80 290,70"
      strokeDasharray="400"
      strokeDashoffset="0"
      style={{animation: 'drawLine 3s ease-in-out infinite'}}
    />
    
    <polyline
      fill="none"
      stroke="#3b82f6"
      strokeWidth="2"
      strokeDasharray="4 2"
      points="30,115 82,105 134,110 186,95 238,105 290,100"
      style={{animation: 'drawLine 3.5s ease-in-out infinite'}}
    />
    
    {[
      {x: 30, y: 95, color: '#ef4444', delay: '0.3s'},
      {x: 82, y: 75, color: '#ef4444', delay: '0.5s'},
      {x: 134, y: 85, color: '#ef4444', delay: '0.7s'},
      {x: 186, y: 65, color: '#ef4444', delay: '0.9s'},
      {x: 238, y: 80, color: '#ef4444', delay: '1.1s'},
      {x: 290, y: 70, color: '#ef4444', delay: '1.3s'}
    ].map((point, i) => (
      <circle 
        key={i} 
        cx={point.x} 
        cy={point.y} 
        r="3.5" 
        fill={point.color} 
        stroke="white" 
        strokeWidth="1.5"
        style={{animation: `scaleIn 0.4s ease-out ${point.delay} both`}}
      />
    ))}
    
    {[
      {x: 186, y: 65, label: '!', color: '#f59e0b'}
    ].map((rage, i) => (
      <g key={i} style={{animation: 'pulse 2s ease-in-out 1.5s infinite'}}>
        <circle cx={rage.x} cy={rage.y - 12} r="6" fill={rage.color} opacity="0.9"/>
        <text x={rage.x} y={rage.y - 9} textAnchor="middle" fill="white" fontSize="8" fontWeight="700">{rage.label}</text>
      </g>
    ))}
    
    <text x="30" y="160" fill="#64748b" fontSize="7">Hero</text>
    <text x="82" y="160" fill="#64748b" fontSize="7">Nav</text>
    <text x="134" y="160" fill="#64748b" fontSize="7">Features</text>
    <text x="186" y="160" fill="#64748b" fontSize="7">Pricing</text>
    <text x="238" y="160" fill="#64748b" fontSize="7">CTA</text>
    <text x="290" y="160" fill="#64748b" fontSize="7">Footer</text>
    
    <rect x="35" y="167" width="8" height="2" fill="#ef4444"/>
    <text x="48" y="171" fill="#64748b" fontSize="7">Scroll depth</text>
    
    <line x1="110" y1="168" x2="118" y2="168" stroke="#3b82f6" strokeWidth="2" strokeDasharray="2 1"/>
    <text x="123" y="171" fill="#64748b" fontSize="7">Engagement</text>
    
    <circle cx="200" cy="168" r="3" fill="#f59e0b"/>
    <text x="207" y="171" fill="#64748b" fontSize="7">Rage clicks</text>
    
    <text x="160" y="189" textAnchor="middle" fill="#64748b" fontSize="9" fontWeight="600">Scroll hesitations & rage clicks per element</text>
  </svg>
);

const AreaChartVisual = () => (
  <svg viewBox="0 0 320 200" className="visual-frame" role="img" aria-label="Area chart preview">
    <rect width="320" height="200" fill="#f8fafc" rx="8"/>
    <rect x="10" y="10" width="300" height="180" fill="white" rx="4"/>
    
    <text x="160" y="28" textAnchor="middle" fill="#1e293b" fontSize="11" fontWeight="700">Revenue Trajectory</text>
    
    <defs>
      <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#6366f1" stopOpacity="0.6" />
        <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
      </linearGradient>
    </defs>
    
    {[0,1,2,3,4,5,6].map((i) => (
      <line key={i} x1={30 + i*40} x2={30 + i*40} y1="40" y2="160" stroke="#e5e7eb" strokeWidth="1" />
    ))}
    {[0,1,2,3,4].map((i) => (
      <line key={i} x1="30" x2="270" y1={40 + i*30} y2={40 + i*30} stroke="#e5e7eb" strokeWidth="1" />
    ))}
    
    <polyline
      fill="url(#areaGradient)"
      stroke="#6366f1"
      strokeWidth="3"
      points="30,130 70,120 110,90 150,100 190,70 230,85 270,55 270,160 30,160"
      strokeDasharray="500"
      strokeDashoffset="0"
      style={{animation: 'drawLine 3s ease-in-out infinite'}}
    />
    
    <circle cx="110" cy="90" r="4" fill="#6366f1" stroke="white" strokeWidth="2" style={{animation: 'pulse 2s ease-in-out infinite'}}/>
    <circle cx="190" cy="70" r="4" fill="#6366f1" stroke="white" strokeWidth="2" style={{animation: 'pulse 2s ease-in-out 0.3s infinite'}}/>
    <circle cx="270" cy="55" r="4" fill="#6366f1" stroke="white" strokeWidth="2" style={{animation: 'pulse 2s ease-in-out 0.6s infinite'}}/>
    
    <rect x="200" y="45" width="60" height="18" fill="rgba(34,197,94,0.1)" rx="4" stroke="#22c55e" strokeWidth="1" style={{animation: 'pulse 3s ease-in-out infinite'}}/>
    <text x="230" y="57" textAnchor="middle" fill="#22c55e" fontSize="8" fontWeight="600" style={{animation: 'pulse 3s ease-in-out infinite'}}>↑ 23% growth</text>
    
    <text x="30" y="178" fill="#64748b" fontSize="7">Jan</text>
    <text x="110" y="178" fill="#64748b" fontSize="7">Mar</text>
    <text x="190" y="178" fill="#64748b" fontSize="7">May</text>
    <text x="270" y="178" fill="#64748b" fontSize="7">Jul</text>
    
    <text x="160" y="195" textAnchor="middle" fill="#64748b" fontSize="8" fontWeight="600">Track revenue trends in real-time</text>
  </svg>
);

const BarChartVisual = () => (
  <svg viewBox="0 0 320 200" className="visual-frame" role="img" aria-label="Bar chart preview">
    <rect width="320" height="200" fill="#f8fafc" rx="8"/>
    <rect x="10" y="10" width="300" height="180" fill="white" rx="4"/>
    
    <text x="160" y="28" textAnchor="middle" fill="#1e293b" fontSize="11" fontWeight="700">Marketing Channels</text>
    
    <line x1="30" x2="30" y1="45" y2="160" stroke="#cbd5e1" strokeWidth="2"/>
    <line x1="30" x2="290" y1="160" y2="160" stroke="#cbd5e1" strokeWidth="2"/>
    
    {[0,1,2,3].map((i) => (
      <line key={i} x1="30" x2="290" y1={45 + i*28.75} y2={45 + i*28.75} stroke="#e5e7eb" strokeWidth="1" />
    ))}
    
    <rect x="50" y="75" width="35" height="85" rx="4" fill="#3b82f6" opacity="0.85" style={{animation: 'liveLoop 4s ease-in-out infinite'}}/>
    <text x="67" y="70" textAnchor="middle" fill="#1e40af" fontSize="9" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 0.1s infinite'}}>52%</text>
    
    <rect x="100" y="90" width="35" height="70" rx="4" fill="#8b5cf6" opacity="0.85" style={{animation: 'liveLoop 4s ease-in-out 0.2s infinite'}}/>
    <text x="117" y="85" textAnchor="middle" fill="#6b21a8" fontSize="9" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 0.3s infinite'}}>38%</text>
    
    <rect x="150" y="110" width="35" height="50" rx="4" fill="#ec4899" opacity="0.85" style={{animation: 'liveLoop 4s ease-in-out 0.4s infinite'}}/>
    <text x="167" y="105" textAnchor="middle" fill="#9f1239" fontSize="9" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 0.5s infinite'}}>25%</text>
    
    <rect x="200" y="125" width="35" height="35" rx="4" fill="#f59e0b" opacity="0.85" style={{animation: 'liveLoop 4s ease-in-out 0.6s infinite'}}/>
    <text x="217" y="120" textAnchor="middle" fill="#92400e" fontSize="9" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 0.7s infinite'}}>18%</text>
    
    <rect x="250" y="140" width="35" height="20" rx="4" fill="#10b981" opacity="0.85" style={{animation: 'liveLoop 4s ease-in-out 0.8s infinite'}}/>
    <text x="267" y="135" textAnchor="middle" fill="#065f46" fontSize="9" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 0.9s infinite'}}>12%</text>
    
    <text x="67" y="175" textAnchor="middle" fill="#64748b" fontSize="7">Google</text>
    <text x="117" y="175" textAnchor="middle" fill="#64748b" fontSize="7">Social</text>
    <text x="167" y="175" textAnchor="middle" fill="#64748b" fontSize="7">Email</text>
    <text x="217" y="175" textAnchor="middle" fill="#64748b" fontSize="7">Direct</text>
    <text x="267" y="175" textAnchor="middle" fill="#64748b" fontSize="7">Referral</text>
    
    <text x="160" y="195" textAnchor="middle" fill="#64748b" fontSize="8" fontWeight="600">Compare traffic sources & ROI</text>
  </svg>
);

// Feature Visual Components
const HeatmapFeatureVisual = () => (
  <svg viewBox="0 0 400 280" className="feature-visual" role="img" aria-label="Heatmap feature">
    <rect width="400" height="280" fill="#f8fafc" rx="8"/>
    <rect x="20" y="20" width="360" height="240" fill="white" rx="4"/>
    
    <rect x="40" y="40" width="320" height="200" fill="#fafafa" rx="4"/>
    
    <rect x="50" y="50" width="300" height="30" fill="#6366f1" rx="4"/>
    <text x="200" y="70" textAnchor="middle" fill="white" fontSize="12" fontWeight="600">Asuma Analytics</text>
    
    <rect x="50" y="90" width="140" height="60" fill="#e0e7ff" rx="4"/>
    <text x="120" y="115" textAnchor="middle" fill="#4338ca" fontSize="10">Hero Section</text>
    <text x="120" y="130" textAnchor="middle" fill="#6366f1" fontSize="8">Start Free Trial</text>
    
    <rect x="210" y="90" width="140" height="60" fill="#f3f4f6" rx="4"/>
    <text x="280" y="115" textAnchor="middle" fill="#64748b" fontSize="10">Features</text>
    
    <rect x="50" y="160" width="300" height="40" fill="#f3f4f6" rx="4"/>
    <text x="200" y="183" textAnchor="middle" fill="#64748b" fontSize="9">Pricing Section</text>
    
    <rect x="50" y="210" width="300" height="20" fill="#1e293b" rx="4"/>
    <text x="200" y="223" textAnchor="middle" fill="white" fontSize="8">Footer</text>
    
    <rect x="50" y="90" width="140" height="60" fill="#ef4444" opacity="0.6" rx="4" style={{animation: 'liveLoop 4s ease-in-out infinite'}}/>
    <circle cx="120" cy="120" r="25" fill="#ef4444" opacity="0.4" style={{animation: 'pulse 2s ease-in-out infinite'}}/>
    <circle cx="120" cy="120" r="15" fill="#ef4444" opacity="0.6" style={{animation: 'pulse 2.5s ease-in-out 0.3s infinite'}}/>
    <text x="120" y="125" textAnchor="middle" fill="white" fontSize="11" fontWeight="700" style={{animation: 'liveLoop 4s ease-in-out 0.2s infinite'}}>87%</text>
    
    <rect x="210" y="90" width="140" height="60" fill="#f59e0b" opacity="0.3" rx="4" style={{animation: 'liveLoop 4s ease-in-out 0.3s infinite'}}/>
    <circle cx="280" cy="120" r="18" fill="#f59e0b" opacity="0.5" style={{animation: 'pulse 2s ease-in-out 0.5s infinite'}}/>
    <text x="280" y="125" textAnchor="middle" fill="#92400e" fontSize="10" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 0.4s infinite'}}>42%</text>
    
    <rect x="50" y="160" width="300" height="40" fill="#3b82f6" opacity="0.25" rx="4" style={{animation: 'liveLoop 4s ease-in-out 0.5s infinite'}}/>
    <circle cx="200" cy="180" r="12" fill="#3b82f6" opacity="0.4" style={{animation: 'pulse 2s ease-in-out 0.7s infinite'}}/>
    <text x="200" y="185" textAnchor="middle" fill="#1e40af" fontSize="9" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 0.6s infinite'}}>58%</text>
    
    <rect x="365" y="30" width="10" height="210" fill="#e5e7eb" rx="2"/>
    <rect x="365" y="30" width="10" height="120" fill="#ef4444" opacity="0.7" rx="2"/>
    <text x="390" y="95" fill="#64748b" fontSize="8">Hot</text>
    <rect x="365" y="150" width="10" height="50" fill="#f59e0b" opacity="0.7" rx="2"/>
    <text x="390" y="175" fill="#64748b" fontSize="8">Warm</text>
    <rect x="365" y="200" width="10" height="40" fill="#3b82f6" opacity="0.7" rx="2"/>
    <text x="390" y="220" fill="#64748b" fontSize="8">Cool</text>
    
    <text x="200" y="270" textAnchor="middle" fill="#64748b" fontSize="12" fontWeight="600">Scroll Heatmap Overlay</text>
  </svg>
);

const SessionRecordingVisual = () => (
  <svg viewBox="0 0 400 280" className="feature-visual" role="img" aria-label="Session recording">
    <rect width="400" height="280" fill="#f8fafc" rx="8"/>
    <rect x="20" y="20" width="360" height="200" fill="#1e293b" rx="4"/>
    
    <rect x="30" y="30" width="340" height="140" fill="white" rx="4"/>
    <rect x="40" y="40" width="320" height="25" fill="#6366f1" rx="3"/>
    <text x="200" y="56" textAnchor="middle" fill="white" fontSize="11" fontWeight="600">asuma.com/pricing</text>
    
    <circle cx="100" cy="100" r="8" fill="#ef4444" opacity="0.8" style={{animation: 'pulse 2s ease-in-out infinite'}}/>
    <circle cx="100" cy="100" r="15" fill="#ef4444" opacity="0.3" style={{animation: 'pulse 2s ease-in-out 0.3s infinite'}}/>
    <text x="100" y="135" textAnchor="middle" fill="#64748b" fontSize="8">Click 1</text>
    
    <path d="M 100 100 Q 150 90, 200 110" stroke="#6366f1" strokeWidth="2" fill="none" strokeDasharray="4 2" opacity="0.6"/>
    
    <circle cx="200" cy="110" r="8" fill="#f59e0b" opacity="0.8" style={{animation: 'pulse 2s ease-in-out 0.5s infinite'}}/>
    <circle cx="200" cy="110" r="15" fill="#f59e0b" opacity="0.3" style={{animation: 'pulse 2s ease-in-out 0.8s infinite'}}/>
    <text x="200" y="145" textAnchor="middle" fill="#64748b" fontSize="8">Scroll</text>
    
    <path d="M 200 110 Q 250 105, 300 120" stroke="#6366f1" strokeWidth="2" fill="none" opacity="0.6" strokeDasharray="150" strokeDashoffset="0" style={{animation: 'drawLine 3s ease-in-out infinite'}}/>
    
    <circle cx="300" cy="120" r="8" fill="#10b981" opacity="0.8" style={{animation: 'pulse 2s ease-in-out 1s infinite'}}/>
    <circle cx="300" cy="120" r="15" fill="#10b981" opacity="0.3" style={{animation: 'pulse 2s ease-in-out 1.3s infinite'}}/>
    <text x="300" y="155" textAnchor="middle" fill="#64748b" fontSize="8">Click 2</text>
    
    <rect x="30" y="180" width="340" height="30" fill="#0f172a" rx="4"/>
    
    <circle cx="50" cy="195" r="8" fill="#6366f1"/>
    <polygon points="47,192 47,198 53,195" fill="white"/>
    
    <rect x="70" y="190" width="240" height="10" fill="#334155" rx="5"/>
    <rect x="70" y="190" width="140" height="10" fill="#6366f1" rx="5">
      <animate attributeName="width" values="0;140;140;0" dur="4s" repeatCount="indefinite"/>
    </rect>
    <circle cx="210" cy="195" r="7" fill="white" stroke="#6366f1" strokeWidth="2">
      <animate attributeName="cx" values="70;210;210;70" dur="4s" repeatCount="indefinite"/>
    </circle>
    
    <text x="320" y="198" fill="#94a3b8" fontSize="10">2:34 / 5:12</text>
    
    <rect x="340" y="187" width="20" height="16" fill="#334155" rx="2"/>
    <rect x="343" y="190" width="3" height="10" fill="#94a3b8" rx="1"/>
    <rect x="348" y="190" width="3" height="10" fill="#94a3b8" rx="1"/>
    <rect x="353" y="190" width="3" height="10" fill="#94a3b8" rx="1"/>
    
    <rect x="30" y="225" width="80" height="18" fill="rgba(99,102,241,0.1)" rx="4" stroke="#6366f1" strokeWidth="1"/>
    <text x="70" y="237" textAnchor="middle" fill="#6366f1" fontSize="9" fontWeight="600">User: Mobile</text>
    
    <rect x="120" y="225" width="90" height="18" fill="rgba(239,68,68,0.1)" rx="4" stroke="#ef4444" strokeWidth="1"/>
    <text x="165" y="237" textAnchor="middle" fill="#ef4444" fontSize="9" fontWeight="600">Rage Click: 3</text>
    
    <rect x="220" y="225" width="70" height="18" fill="rgba(34,197,94,0.1)" rx="4" stroke="#22c55e" strokeWidth="1"/>
    <text x="255" y="237" textAnchor="middle" fill="#22c55e" fontSize="9" fontWeight="600">Converted</text>
    
    <text x="200" y="270" textAnchor="middle" fill="#64748b" fontSize="12" fontWeight="600">Session Replay with User Journey</text>
  </svg>
);

const AICopilotVisual = () => (
  <svg viewBox="0 0 400 280" className="feature-visual" role="img" aria-label="AI Copilot">
    <rect width="400" height="280" fill="#f8fafc" rx="8"/>
    <rect x="20" y="20" width="360" height="240" fill="white" rx="4"/>
    <circle cx="60" cy="60" r="20" fill="#8b5cf6" opacity="0.2" style={{animation: 'pulse 2s ease-in-out infinite'}}/>
    <text x="60" y="67" textAnchor="middle" fill="#8b5cf6" fontSize="20">🤖</text>
    <rect x="90" y="45" width="280" height="30" fill="#f3f4f6" rx="4" style={{animation: 'liveLoop 4s ease-in-out infinite'}}/>
    <text x="100" y="65" fill="#64748b" fontSize="12" style={{animation: 'liveLoop 4s ease-in-out 0.1s infinite'}}>AI detected 3 high-impact issues</text>
    <rect x="90" y="90" width="280" height="50" fill="#fef3c7" rx="4" style={{animation: 'liveLoop 4s ease-in-out 0.2s infinite'}}/>
    <text x="100" y="110" fill="#92400e" fontSize="11" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 0.3s infinite'}}>⚠️ 45% users drop at checkout</text>
    <text x="100" y="128" fill="#78350f" fontSize="10" style={{animation: 'liveLoop 4s ease-in-out 0.4s infinite'}}>Recommendation: Add trust badges</text>
    <rect x="90" y="150" width="280" height="40" fill="#dbeafe" rx="4" style={{animation: 'liveLoop 4s ease-in-out 0.3s infinite'}}/>
    <text x="100" y="170" fill="#1e40af" fontSize="11" style={{animation: 'liveLoop 4s ease-in-out 0.5s infinite'}}>💡 CTA button has low visibility</text>
    <text x="200" y="270" textAnchor="middle" fill="#64748b" fontSize="12" fontWeight="600">AI-Powered Insights</text>
  </svg>
);

const FunnelVisual = () => (
  <svg viewBox="0 0 400 280" className="feature-visual" role="img" aria-label="Conversion funnel">
    <rect width="400" height="280" fill="#f8fafc" rx="8"/>
    <rect x="20" y="20" width="360" height="240" fill="white" rx="4"/>
    
    <text x="200" y="45" textAnchor="middle" fill="#1e293b" fontSize="13" fontWeight="700">Conversion Journey</text>
    
    <circle cx="50" cy="75" r="8" fill="#10b981" style={{animation: 'pulse 2s ease-in-out infinite'}}/>
    <text x="50" y="79" textAnchor="middle" fill="white" fontSize="9" fontWeight="700" style={{animation: 'liveLoop 4s ease-in-out infinite'}}>1</text>
    <rect x="70" y="60" width="250" height="30" fill="#f0fdf4" rx="6" stroke="#10b981" strokeWidth="2" style={{animation: 'liveLoop 4s ease-in-out infinite'}}/>
    <text x="85" y="75" fill="#15803d" fontSize="11" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 0.1s infinite'}}>Landing Page</text>
    <text x="85" y="88" fill="#15803d" fontSize="9" style={{animation: 'liveLoop 4s ease-in-out 0.2s infinite'}}>1,000 visitors</text>
    <rect x="240" y="67" width="70" height="16" fill="#10b981" rx="4" style={{animation: 'pulse 2s ease-in-out 0.3s infinite'}}/>
    <text x="275" y="78" textAnchor="middle" fill="white" fontSize="10" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 0.3s infinite'}}>100%</text>
    
    <line x1="50" y1="83" x2="50" y2="105" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="4 2"/>
    
    <circle cx="50" cy="120" r="8" fill="#3b82f6" style={{animation: 'pulse 2s ease-in-out 0.5s infinite'}}/>
    <text x="50" y="124" textAnchor="middle" fill="white" fontSize="9" fontWeight="700" style={{animation: 'liveLoop 4s ease-in-out 0.4s infinite'}}>2</text>
    <rect x="70" y="105" width="250" height="30" fill="#eff6ff" rx="6" stroke="#3b82f6" strokeWidth="2" style={{animation: 'liveLoop 4s ease-in-out 0.4s infinite'}}/>
    <text x="85" y="120" fill="#1e40af" fontSize="11" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 0.5s infinite'}}>Sign Up</text>
    <text x="85" y="133" fill="#1e40af" fontSize="9" style={{animation: 'liveLoop 4s ease-in-out 0.6s infinite'}}>450 users</text>
    <rect x="240" y="112" width="70" height="16" fill="#3b82f6" rx="4" style={{animation: 'pulse 2s ease-in-out 0.6s infinite'}}/>
    <text x="275" y="123" textAnchor="middle" fill="white" fontSize="10" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 0.7s infinite'}}>45%</text>
    <rect x="330" y="112" width="45" height="16" fill="rgba(239,68,68,0.1)" rx="4" stroke="#ef4444" strokeWidth="1" style={{animation: 'pulse 2s ease-in-out 0.8s infinite'}}/>
    <text x="352" y="123" textAnchor="middle" fill="#ef4444" fontSize="9" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 0.8s infinite'}}>-55%</text>
    
    <line x1="50" y1="128" x2="50" y2="150" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="4 2"/>
    
    <circle cx="50" cy="165" r="8" fill="#f59e0b" style={{animation: 'pulse 2s ease-in-out 1s infinite'}}/>
    <text x="50" y="169" textAnchor="middle" fill="white" fontSize="9" fontWeight="700" style={{animation: 'liveLoop 4s ease-in-out 0.9s infinite'}}>3</text>
    <rect x="70" y="150" width="250" height="30" fill="#fef3c7" rx="6" stroke="#f59e0b" strokeWidth="2" style={{animation: 'liveLoop 4s ease-in-out 0.9s infinite'}}/>
    <text x="85" y="165" fill="#92400e" fontSize="11" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 1s infinite'}}>Add to Cart</text>
    <text x="85" y="178" fill="#92400e" fontSize="9" style={{animation: 'liveLoop 4s ease-in-out 1.1s infinite'}}>220 users</text>
    <rect x="240" y="157" width="70" height="16" fill="#f59e0b" rx="4" style={{animation: 'pulse 2s ease-in-out 1.1s infinite'}}/>
    <text x="275" y="168" textAnchor="middle" fill="white" fontSize="10" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 1.2s infinite'}}>22%</text>
    <rect x="330" y="157" width="45" height="16" fill="rgba(239,68,68,0.1)" rx="4" stroke="#ef4444" strokeWidth="1" style={{animation: 'pulse 2s ease-in-out 1.3s infinite'}}/>
    <text x="352" y="168" textAnchor="middle" fill="#ef4444" fontSize="9" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 1.3s infinite'}}>-51%</text>
    
    <line x1="50" y1="173" x2="50" y2="195" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="4 2"/>
    
    <circle cx="50" cy="210" r="8" fill="#8b5cf6" style={{animation: 'pulse 2s ease-in-out 1.5s infinite'}}/>
    <text x="50" y="214" textAnchor="middle" fill="white" fontSize="9" fontWeight="700" style={{animation: 'liveLoop 4s ease-in-out 1.4s infinite'}}>4</text>
    <rect x="70" y="195" width="250" height="30" fill="#f3e8ff" rx="6" stroke="#8b5cf6" strokeWidth="2" style={{animation: 'liveLoop 4s ease-in-out 1.4s infinite'}}/>
    <text x="85" y="210" fill="#6b21a8" fontSize="11" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 1.5s infinite'}}>Purchase</text>
    <text x="85" y="223" fill="#6b21a8" fontSize="9" style={{animation: 'liveLoop 4s ease-in-out 1.6s infinite'}}>180 conversions</text>
    <rect x="240" y="202" width="70" height="16" fill="#8b5cf6" rx="4" style={{animation: 'pulse 2s ease-in-out 1.6s infinite'}}/>
    <text x="275" y="213" textAnchor="middle" fill="white" fontSize="10" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 1.7s infinite'}}>18%</text>
    <rect x="330" y="202" width="45" height="16" fill="rgba(239,68,68,0.1)" rx="4" stroke="#ef4444" strokeWidth="1" style={{animation: 'pulse 2s ease-in-out 1.8s infinite'}}/>
    <text x="352" y="213" textAnchor="middle" fill="#ef4444" fontSize="9" fontWeight="600" style={{animation: 'liveLoop 4s ease-in-out 1.8s infinite'}}>-18%</text>
    
    <text x="200" y="250" textAnchor="middle" fill="#64748b" fontSize="11" fontWeight="600">Step-by-Step Conversion Flow</text>
  </svg>
);

const FormAnalyticsVisual = () => (
  <svg viewBox="0 0 400 280" className="feature-visual" role="img" aria-label="Form analytics">
    <rect width="400" height="280" fill="#f8fafc" rx="8"/>
    <rect x="20" y="20" width="360" height="240" fill="white" rx="4"/>
    
    <text x="200" y="45" textAnchor="middle" fill="#1e293b" fontSize="13" fontWeight="700">Form Field Completion</text>
    
    <text x="40" y="75" fill="#64748b" fontSize="11" fontWeight="600">Email</text>
    <rect x="100" y="63" width="260" height="18" fill="#e5e7eb" rx="9"/>
    <rect x="100" y="63" width="247" height="18" fill="#10b981" rx="9">
      <animate attributeName="width" values="0;247;247;0" dur="3s" repeatCount="indefinite"/>
    </rect>
    <text x="370" y="76" fill="#10b981" fontSize="10" fontWeight="700">95%</text>
    
    <text x="40" y="110" fill="#64748b" fontSize="11" fontWeight="600">Name</text>
    <rect x="100" y="98" width="260" height="18" fill="#e5e7eb" rx="9"/>
    <rect x="100" y="98" width="234" height="18" fill="#22c55e" rx="9">
      <animate attributeName="width" values="0;234;234;0" dur="3s" begin="0.2s" repeatCount="indefinite"/>
    </rect>
    <text x="370" y="111" fill="#22c55e" fontSize="10" fontWeight="700">90%</text>
    
    <text x="40" y="145" fill="#64748b" fontSize="11" fontWeight="600">Phone</text>
    <rect x="100" y="133" width="260" height="18" fill="#e5e7eb" rx="9"/>
    <rect x="100" y="133" width="182" height="18" fill="#3b82f6" rx="9">
      <animate attributeName="width" values="0;182;182;0" dur="3s" begin="0.4s" repeatCount="indefinite"/>
    </rect>
    <text x="370" y="146" fill="#3b82f6" fontSize="10" fontWeight="700">70%</text>
    
    <text x="40" y="180" fill="#64748b" fontSize="11" fontWeight="600">Address</text>
    <rect x="100" y="168" width="260" height="18" fill="#e5e7eb" rx="9"/>
    <rect x="100" y="168" width="130" height="18" fill="#f59e0b" rx="9">
      <animate attributeName="width" values="0;130;130;0" dur="3s" begin="0.6s" repeatCount="indefinite"/>
    </rect>
    <text x="370" y="181" fill="#f59e0b" fontSize="10" fontWeight="700">50%</text>
    
    <text x="40" y="215" fill="#64748b" fontSize="11" fontWeight="600">Payment</text>
    <rect x="100" y="203" width="260" height="18" fill="#e5e7eb" rx="9"/>
    <rect x="100" y="203" width="78" height="18" fill="#ef4444" rx="9">
      <animate attributeName="width" values="0;78;78;0" dur="3s" begin="0.8s" repeatCount="indefinite"/>
    </rect>
    <text x="370" y="216" fill="#ef4444" fontSize="10" fontWeight="700">30%</text>
    
    <line x1="100" y1="235" x2="360" y2="235" stroke="#cbd5e1" strokeWidth="1"/>
    <text x="100" y="248" fill="#94a3b8" fontSize="8">0%</text>
    <text x="360" y="248" textAnchor="end" fill="#94a3b8" fontSize="8">100%</text>
    
    <text x="200" y="270" textAnchor="middle" fill="#64748b" fontSize="11" fontWeight="600">Animated Completion Rates</text>
  </svg>
);

const LiveExperienceScoreVisual = () => (
  <svg viewBox="0 0 400 280" className="feature-visual" role="img" aria-label="Live Experience Score">
    <rect width="400" height="280" fill="#f8fafc" rx="8"/>
    <rect x="20" y="20" width="360" height="240" fill="white" rx="4"/>
    
    <rect x="40" y="40" width="320" height="200" fill="linear-gradient(135deg, #f8fafc, #e0e7ff)" rx="8"/>
    
    <circle cx="200" cy="120" r="70" stroke="#e5e7eb" strokeWidth="12" fill="none"/>
    <circle cx="200" cy="120" r="70" stroke="#6366f1" strokeWidth="12" fill="none" strokeDasharray="351.86 87.96" transform="rotate(-90 200 120)">
      <animate attributeName="stroke-dasharray" values="0 439.82;351.86 87.96" dur="2s" fill="freeze"/>
    </circle>
    
    <text x="200" y="110" textAnchor="middle" fill="#1e293b" fontSize="48" fontWeight="700">92</text>
    <text x="200" y="135" textAnchor="middle" fill="#64748b" fontSize="16">/100</text>
    
    <rect x="60" y="50" width="80" height="24" fill="rgba(239,68,68,0.1)" rx="6" stroke="#ef4444" strokeWidth="1"/>
    <circle cx="75" cy="62" r="4" fill="#ef4444">
      <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite"/>
    </circle>
    <text x="85" y="66" fill="#ef4444" fontSize="11" fontWeight="600">LIVE</text>
    
    <text x="200" y="205" textAnchor="middle" fill="#10b981" fontSize="14" fontWeight="700">Excellent</text>
    <text x="200" y="222" textAnchor="middle" fill="#64748b" fontSize="11">Updated 3 seconds ago</text>
    
    <rect x="50" y="235" width="60" height="4" fill="#e5e7eb" rx="2"/>
    <rect x="50" y="235" width="52" height="4" fill="#10b981" rx="2"/>
    <text x="115" y="239" fill="#64748b" fontSize="9">Performance</text>
    
    <rect x="180" y="235" width="60" height="4" fill="#e5e7eb" rx="2"/>
    <rect x="180" y="235" width="48" height="4" fill="#3b82f6" rx="2"/>
    <text x="245" y="239" fill="#64748b" fontSize="9">Engagement</text>
    
    <rect x="310" y="235" width="40" height="4" fill="#e5e7eb" rx="2"/>
    <rect x="310" y="235" width="36" height="4" fill="#f59e0b" rx="2"/>
    <text x="285" y="239" fill="#64748b" fontSize="9">Errors</text>
    
    <text x="200" y="270" textAnchor="middle" fill="#64748b" fontSize="12" fontWeight="600">Real-Time Experience Monitoring</text>
  </svg>
);

const LiveScoreVisual = () => (
  <svg viewBox="0 0 320 200" className="visual-frame" role="img" aria-label="Live experience score">
    <rect width="320" height="200" fill="#f8fafc" rx="8"/>
    <rect x="10" y="10" width="300" height="180" fill="white" rx="6"/>
    
    <text x="160" y="28" textAnchor="middle" fill="#1e293b" fontSize="11" fontWeight="700">Live Experience Score</text>
    
    <circle cx="160" cy="90" r="45" stroke="#e5e7eb" strokeWidth="8" fill="none"/>
    <circle cx="160" cy="90" r="45" stroke="#6366f1" strokeWidth="8" fill="none" strokeDasharray="230 52" transform="rotate(-90 160 90)">
      <animate attributeName="stroke-dasharray" values="0 282;230 52" dur="2s" fill="freeze"/>
    </circle>
    <text x="160" y="88" textAnchor="middle" fill="#1e293b" fontSize="28" fontWeight="700">92</text>
    <text x="160" y="105" textAnchor="middle" fill="#64748b" fontSize="11">/100</text>
    
    <rect x="120" y="145" width="80" height="16" fill="rgba(16,185,129,0.1)" rx="4" stroke="#10b981" strokeWidth="1"/>
    <text x="160" y="156" textAnchor="middle" fill="#10b981" fontSize="9" fontWeight="600">Excellent Health</text>
    
    <circle cx="35" cy="25" r="6" fill="#ef4444">
      <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite"/>
    </circle>
    <text x="45" y="29" fill="#ef4444" fontSize="9" fontWeight="600">LIVE</text>
    
    <text x="160" y="178" textAnchor="middle" fill="#64748b" fontSize="8" fontWeight="600">Real-time UX health monitoring</text>
    <text x="160" y="191" textAnchor="middle" fill="#94a3b8" fontSize="7">Updates every 5 seconds via API</text>
  </svg>
);

const VoiceAIVisual = () => (
  <svg viewBox="0 0 320 200" className="visual-frame" role="img" aria-label="Voice AI assistant">
    <rect width="320" height="200" fill="#f8fafc" rx="8"/>
    <rect x="10" y="10" width="300" height="180" fill="white" rx="6"/>
    
    <text x="160" y="28" textAnchor="middle" fill="#1e293b" fontSize="11" fontWeight="700">AI Voice Assistant</text>
    
    <circle cx="160" cy="75" r="30" fill="#8b5cf6" opacity="0.1">
      <animate attributeName="r" values="30;35;30" dur="2s" repeatCount="indefinite"/>
      <animate attributeName="opacity" values="0.1;0.05;0.1" dur="2s" repeatCount="indefinite"/>
    </circle>
    <circle cx="160" cy="75" r="22" fill="#8b5cf6" opacity="0.2">
      <animate attributeName="r" values="22;26;22" dur="2s" repeatCount="indefinite"/>
      <animate attributeName="opacity" values="0.2;0.1;0.2" dur="2s" repeatCount="indefinite"/>
    </circle>
    <circle cx="160" cy="75" r="14" fill="#8b5cf6" opacity="0.3">
      <animate attributeName="r" values="14;17;14" dur="2s" repeatCount="indefinite"/>
      <animate attributeName="opacity" values="0.3;0.15;0.3" dur="2s" repeatCount="indefinite"/>
    </circle>
    <text x="160" y="82" textAnchor="middle" fontSize="20">🎤</text>
    
    <rect x="30" y="115" width="260" height="45" fill="#f3f4f6" rx="6"/>
    <text x="160" y="132" textAnchor="middle" fill="#6366f1" fontSize="10" fontWeight="600">"Show me visitors rate for my online shop"</text>
    <text x="160" y="148" textAnchor="middle" fill="#64748b" fontSize="8">Processing voice command...</text>
    
    {[0,1,2,3,4].map((i) => (
      <rect key={i} x={145 + i * 8} y={153 - (i % 2) * 3} width="5" height={6 + (i % 2) * 3} fill="#8b5cf6" rx="1.5">
        <animate attributeName="height" values="6;12;6" dur="1s" begin={`${i * 0.1}s`} repeatCount="indefinite"/>
        <animate attributeName="y" values="153;150;153" dur="1s" begin={`${i * 0.1}s`} repeatCount="indefinite"/>
      </rect>
    ))}
    
    <text x="160" y="178" textAnchor="middle" fill="#64748b" fontSize="8" fontWeight="600">Ask questions using your voice</text>
    <text x="160" y="191" textAnchor="middle" fill="#94a3b8" fontSize="7">No typing or clicking required</text>
  </svg>
);

const UserJourneyVisual = () => (
  <svg viewBox="0 0 320 200" className="visual-frame" role="img" aria-label="User journey tracking">
    <rect width="320" height="200" fill="#f8fafc" rx="8"/>
    <rect x="10" y="10" width="300" height="180" fill="white" rx="6"/>
    
    <text x="160" y="28" textAnchor="middle" fill="#1e293b" fontSize="11" fontWeight="700">User Journey Map</text>
    
    <circle cx="35" cy="80" r="10" fill="#10b981" opacity="0.9"/>
    <text x="35" y="84" textAnchor="middle" fill="white" fontSize="9" fontWeight="700">1</text>
    <line x1="45" y1="80" x2="75" y2="80" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="3 2"/>
    
    <circle cx="85" cy="80" r="10" fill="#3b82f6" opacity="0.9"/>
    <text x="85" y="84" textAnchor="middle" fill="white" fontSize="9" fontWeight="700">2</text>
    <line x1="95" y1="80" x2="125" y2="80" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="3 2"/>
    
    <circle cx="135" cy="80" r="10" fill="#f59e0b" opacity="0.9"/>
    <text x="135" y="84" textAnchor="middle" fill="white" fontSize="9" fontWeight="700">3</text>
    <line x1="145" y1="80" x2="175" y2="80" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="3 2"/>
    
    <circle cx="185" cy="80" r="10" fill="#8b5cf6" opacity="0.9"/>
    <text x="185" y="84" textAnchor="middle" fill="white" fontSize="9" fontWeight="700">4</text>
    <line x1="195" y1="80" x2="225" y2="80" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="3 2"/>
    
    <circle cx="235" cy="80" r="10" fill="#ec4899" opacity="0.9"/>
    <text x="235" y="84" textAnchor="middle" fill="white" fontSize="9" fontWeight="700">5</text>
    <line x1="245" y1="80" x2="275" y2="80" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="3 2"/>
    
    <circle cx="285" cy="80" r="10" fill="#22c55e" opacity="0.9"/>
    <text x="285" y="84" textAnchor="middle" fill="white" fontSize="9" fontWeight="700">✓</text>
    
    <text x="35" y="105" textAnchor="middle" fill="#64748b" fontSize="7">Home</text>
    <text x="85" y="105" textAnchor="middle" fill="#64748b" fontSize="7">Product</text>
    <text x="135" y="105" textAnchor="middle" fill="#64748b" fontSize="7">Cart</text>
    <text x="185" y="105" textAnchor="middle" fill="#64748b" fontSize="7">Checkout</text>
    <text x="235" y="105" textAnchor="middle" fill="#64748b" fontSize="7">Payment</text>
    <text x="285" y="105" textAnchor="middle" fill="#64748b" fontSize="7">Success</text>
    
    <rect x="60" y="125" width="200" height="24" fill="#f0fdf4" rx="5" stroke="#22c55e" strokeWidth="1"/>
    <text x="160" y="140" textAnchor="middle" fill="#15803d" fontSize="9" fontWeight="600">✓ Converted in 3m 42s</text>
    
    <text x="160" y="165" textAnchor="middle" fill="#64748b" fontSize="8" fontWeight="600">Track complete user paths</text>
    <text x="160" y="178" textAnchor="middle" fill="#94a3b8" fontSize="7">From landing to conversion</text>
  </svg>
);

const VISUAL_GALLERY = [
  {
    title: 'Engagement heatmap',
    description: 'Pinpoint scroll hesitations and rage clicks per element.',
    visual: <HeatmapVisual />,
    detailedTitle: 'Engagement Heatmap Analytics',
    detailedDescription: 'Visualize exactly where users click, scroll, and interact on your website. Our advanced heatmap technology tracks every mouse movement, click, and scroll Behaviour to give you a complete picture of user engagement.',
    features: [
      'Click heatmaps - See where users click most frequently',
      'Scroll depth tracking - Understand how far users scroll on each page',
      'Rage click detection - Identify frustration points automatically',
      'Move maps - Track cursor movement patterns',
      'Attention maps - Visualize time spent on each element',
      'Mobile & desktop views - Separate heatmaps for different devices'
    ],
    benefits: 'Identify high-performing content, optimize page layouts, reduce friction points, and increase engagement by understanding exactly how users interact with your site.'
  },
  {
    title: 'Revenue trajectory',
    description: 'Overlay campaigns with conversions to catch swings early.',
    visual: <AreaChartVisual />,
    detailedTitle: 'Revenue Trajectory Analytics',
    detailedDescription: 'Track your revenue trends in real-time and correlate them with marketing campaigns, product launches, and user Behaviour changes. Our intelligent analytics help you spot opportunities and address issues before they impact your bottom line.',
    features: [
      'Real-time revenue tracking - Monitor income as it happens',
      'Campaign correlation - Link revenue to specific marketing efforts',
      'Trend analysis - Identify patterns and seasonal variations',
      'Conversion attribution - See which channels drive revenue',
      'Predictive forecasting - AI-powered revenue predictions',
      'Custom date ranges - Compare any time periods'
    ],
    benefits: 'Make data-driven decisions, optimize marketing spend, identify growth opportunities, and catch revenue drops early with instant alerts and AI-powered insights.'
  },
  {
    title: 'Channel comparison',
    description: 'Stack key marketing sources with week-over-week lift.',
    visual: <BarChartVisual />,
    detailedTitle: 'Marketing Channel Comparison',
    detailedDescription: 'Compare performance across all your marketing channels in one unified view. Understand which sources drive the most valuable traffic and optimize your marketing budget accordingly.',
    features: [
      'Multi-channel tracking - Monitor all traffic sources',
      'Week-over-week comparison - Spot trends quickly',
      'ROI calculation - Measure return on marketing spend',
      'User quality metrics - Compare engagement by channel',
      'Conversion rates - Track which channels convert best',
      'Custom channel grouping - Organize sources your way'
    ],
    benefits: 'Optimize marketing budget allocation, identify underperforming channels, double down on what works, and understand the complete customer journey across all touchpoints.'
  },
  {
    title: 'Live experience score',
    description: 'Real-time user experience metrics powered by API integration.',
    visual: <LiveScoreVisual />,
    detailedTitle: 'Live Experience Score - Real-Time API Integration',
    detailedDescription: 'Monitor your website\'s user experience in real-time with our advanced API integration. The Live Experience Score aggregates multiple metrics including page load times, interaction delays, error rates, and user satisfaction to give you an instant snapshot of your site\'s health. Data updates every few seconds, ensuring you\'re always aware of your site\'s performance.',
    features: [
      'Real-time API integration - Direct connection to your website for instant data',
      'Automatic score calculation - AI-powered algorithm weighs multiple factors',
      'Live updates - Scores refresh every 5 seconds without page reload',
      'Performance metrics - Page speed, Core Web Vitals, and response times',
      'Error tracking - Instant alerts when issues are detected',
      'Historical comparison - Compare current score to past performance'
    ],
    benefits: 'Stay ahead of issues before they impact users. The real-time API integration means you can monitor launches, detect problems instantly, and validate fixes immediately. Perfect for development teams, product managers, and anyone who needs to ensure optimal user experience 24/7.'
  },
  {
    title: 'Voice AI assistant',
    description: 'Ask questions and get insights using voice commands - no typing needed.',
    visual: <VoiceAIVisual />,
    detailedTitle: 'Voice-to-Text AI Assistant',
    detailedDescription: 'Interact with your analytics using natural voice commands. Our AI-powered voice assistant understands your questions and performs complex analysis without requiring you to type, click, or navigate through menus. Simply speak your request and get instant insights, reports, and visualizations.',
    features: [
      'Voice recognition - Accurate speech-to-text in multiple languages',
      'Natural language processing - Ask questions in plain English',
      'Hands-free operation - Perfect for presentations and multitasking',
      'Complex queries - "Show conversion rate for mobile users last week"',
      'Instant responses - AI processes and visualizes data in seconds',
      'Voice commands - Create reports, export data, set alerts by voice'
    ],
    benefits: 'Save time and increase productivity by eliminating manual navigation. Perfect for busy executives, during presentations, or when you need quick insights on the go. The AI understands context and can handle follow-up questions, making data exploration as natural as having a conversation.'
  },
  {
    title: 'User journey tracking',
    description: 'Visualize complete user paths from entry to conversion.',
    visual: <UserJourneyVisual />,
    detailedTitle: 'User Journey & Behaviour Tracking',
    detailedDescription: 'Map out complete user journeys from first touch to conversion. Track every step users take on your site, understand drop-off points, and optimize the path to purchase. See exactly how users navigate through your site, which pages they visit, how long they stay, and where they exit.',
    features: [
      'Complete journey mapping - Visualize every step from entry to exit',
      'Path analysis - Identify the most common routes to conversion',
      'Drop-off detection - See exactly where users abandon their journey',
      'Time tracking - Monitor how long users spend at each stage',
      'Device & source tracking - Compare journeys across different channels',
      'Conversion attribution - Understand which touchpoints drive success'
    ],
    benefits: 'Optimize your conversion funnel by understanding the complete user journey. Identify bottlenecks, reduce friction, and create smoother paths to conversion. Perfect for e-commerce sites, SaaS platforms, and any business focused on improving user experience and conversion rates.'
  }
];

function VisualGallery() {
  const [selectedFeature, setSelectedFeature] = useState(null);

  const openModal = (feature) => {
    setSelectedFeature(feature);
  };

  const closeModal = () => {
    setSelectedFeature(null);
  };

  return (
    <>
      <section className="visual-gallery container">
        <div className="section-head">
          <p className="eyebrow">Live intelligence</p>
          <h2 className="section-title">Heatmaps, graphs, and charts engineered for clarity</h2>
          <p className="section-sub">Every visualization streams in real time so you can orient the team in seconds.</p>
        </div>
        <div className="visual-grid">
          {VISUAL_GALLERY.map((item) => (
            <article className="visual-card" key={item.title}>
              {item.visual}
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              <button 
                className="btn btn-ghost" 
                style={{ marginTop: '12px', width: '100%' }}
                onClick={() => openModal(item)}
              >
                View Details
              </button>
            </article>
          ))}
        </div>
      </section>

      {selectedFeature && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            overflowY: 'auto',
            padding: '24px',
          }}
          onClick={closeModal}
        >
          <div
            style={{
              maxWidth: 700,
              width: '100%',
              background: '#020617',
              borderRadius: 16,
              padding: 32,
              boxShadow: '0 24px 70px rgba(15,23,42,0.9)',
              border: '1px solid rgba(148,163,184,0.3)',
              color: '#e5e7eb',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
              <div>
                <p className="eyebrow" style={{ marginBottom: 8 }}>Feature Details</p>
                <h2 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: '#fff' }}>{selectedFeature.detailedTitle}</h2>
              </div>
              <button
                type="button"
                onClick={closeModal}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  fontSize: 28,
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: 24 }}>
              {selectedFeature.visual}
            </div>

            <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#cbd5e1', marginBottom: 24 }}>
              {selectedFeature.detailedDescription}
            </p>

            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#fff', marginBottom: 12 }}>Key Features</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {selectedFeature.features.map((feature, index) => (
                  <li 
                    key={index}
                    style={{
                      padding: '10px 16px',
                      marginBottom: 8,
                      background: 'rgba(100,116,139,0.1)',
                      borderRadius: 8,
                      borderLeft: '3px solid #6366f1',
                      fontSize: '14px',
                      color: '#e2e8f0'
                    }}
                  >
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <div style={{ 
              padding: '16px', 
              background: 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(139,92,246,0.1))',
              borderRadius: 12,
              border: '1px solid rgba(99,102,241,0.2)',
              marginBottom: 24
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#fff', marginBottom: 8 }}>Benefits</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#cbd5e1', margin: 0 }}>
                {selectedFeature.benefits}
              </p>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="button"
                onClick={closeModal}
                className="btn btn-ghost"
                style={{ flex: 1 }}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ flex: 1 }}
                onClick={() => {
                  closeModal();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Metrics() {
  return (
    <section className="metrics container">
      {METRIC_CARDS.map((metric) => (
        <div className="metrics-card" key={metric.label}>
          <span className="metrics-label">{metric.label}</span>
          <strong>{metric.value}</strong>
          <p>{metric.detail}</p>
        </div>
      ))}
    </section>
  );
}

function Features() {
  const [selectedFeature, setSelectedFeature] = useState(null);
  
  const featureVisuals = [
    <HeatmapFeatureVisual />,
    <LiveExperienceScoreVisual />,
    <AICopilotVisual />,
    <SessionRecordingVisual />,
    <FunnelVisual />,
    <FormAnalyticsVisual />
  ];

  const openModal = (feature) => {
    setSelectedFeature(feature);
  };

  const closeModal = () => {
    setSelectedFeature(null);
  };

  return (
    <>
      <section className="features container" id="features">
        <div className="section-head">
          <p className="eyebrow">Why teams pick Asuma</p>
          <h2 className="section-title">Everything You Need to Understand Your Website Visitors</h2>
          <p className="section-sub">
            Asuma gives you a powerful suite of workflow tools to deliver experiences that convert, every time.
          </p>
        </div>
        <div className="feature-grid-cards">
          {FEATURE_LIST.map((feature, index) => (
            <div className="feature-card-item" key={feature.title}>
              <div className="feature-card-visual">
                {featureVisuals[index]}
              </div>
              <div className="feature-card-content">
                <div className="feature-card-header">
                  <h3>{feature.title}</h3>
                  {feature.badge && <span className={feature.badge === 'Coming Soon' ? 'feature-badge feature-badge-soon' : 'feature-badge'}>{feature.badge}</span>}
                </div>
                <p className="feature-card-desc">{feature.desc}</p>
                <button 
                  className="btn btn-ghost" 
                  style={{ marginTop: '16px', width: '100%' }}
                  onClick={() => openModal(feature)}
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {selectedFeature && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            overflowY: 'auto',
            padding: '24px',
          }}
          onClick={closeModal}
        >
          <div
            style={{
              maxWidth: 600,
              width: '100%',
              background: '#020617',
              borderRadius: 16,
              padding: 32,
              boxShadow: '0 24px 70px rgba(15,23,42,0.9)',
              border: '1px solid rgba(148,163,184,0.3)',
              color: '#e5e7eb',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#fff' }}>{selectedFeature.title}</h2>
              </div>
              <button
                type="button"
                onClick={closeModal}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  fontSize: 28,
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                ×
              </button>
            </div>

            <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#cbd5e1', marginBottom: 24 }}>
              {selectedFeature.longDesc}
            </p>

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="button"
                onClick={closeModal}
                className="btn btn-ghost"
                style={{ flex: 1 }}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ flex: 1 }}
                onClick={() => {
                  closeModal();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Integrations() {
  return (
    <section className="integrations container">
      <div className="section-head">
        <p className="eyebrow">Integrations</p>
        <h2 className="section-title">Asuma works with the tools you already use</h2>
        <p className="section-sub">
          Connect Asuma with your favorite platforms and tools. Simple setup, powerful insights.
        </p>
      </div>
      <div className="integrations-grid">
        {INTEGRATIONS.map((integration) => (
          <div className="integration-tile" key={integration.name}>
            <div 
              className="integration-icon" 
              style={{ 
                background: `linear-gradient(135deg, ${integration.color}15, ${integration.color}08)`,
                borderColor: `${integration.color}30`
              }}
            >
              <div 
                className="integration-logo-placeholder"
                style={{ 
                  background: integration.color,
                  opacity: 0.9
                }}
              >
                {integration.name.charAt(0)}
              </div>
            </div>
            <div className="integration-info">
              <h4>{integration.name}</h4>
              <span className="integration-category">{integration.category}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="integrations-footer">
        <p>And more available through our API integrations built for scalability, automation, and commercial-ready web applications.
</p>
      </div>
    </section>
  );
}

function PrivacyCompliance() {
  const navigate = useNavigate();
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactError, setContactError] = useState('');
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactForm, setContactForm] = useState({
    company_name: '',
    contact_name: '',
    business_email: '',
    phone: '',
    website: '',
    company_size: '',
    industry: '',
    message: '',
  });
  const [industryOption, setIndustryOption] = useState('');
  const [industryOther, setIndustryOther] = useState('');

  const openContactModal = () => {
    setShowContactModal(true);
    setContactError('');
    setContactSuccess(false);
  };

  const closeContactModal = () => {
    if (contactSubmitting) return;
    setShowContactModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleIndustryChange = (e) => {
    const value = e.target.value;
    setIndustryOption(value);
    if (value === 'Other') {
      setIndustryOther('');
      setContactForm((prev) => ({ ...prev, industry: '' }));
    } else {
      setIndustryOther('');
      setContactForm((prev) => ({ ...prev, industry: value }));
    }
  };

  const handleIndustryOtherChange = (e) => {
    const value = e.target.value;
    setIndustryOther(value);
    setContactForm((prev) => ({ ...prev, industry: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.company_name || !contactForm.business_email) {
      setContactError('Please provide your company name and business email.');
      return;
    }
    setContactSubmitting(true);
    setContactError('');
    setContactSuccess(false);
    try {
      const rawWebsite = (contactForm.website || '').trim();
      const normalizedWebsite = rawWebsite
        ? `https://${rawWebsite.replace(/^https?:\/\//i, '')}`
        : '';
      const res = await fetch(`${API_BASE}/sales/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...contactForm,
          website: normalizedWebsite,
          plan: 'Demo Request',
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error((data && data.error) || 'Failed to send enquiry');
      }
      setContactSuccess(true);
      setContactForm({
        company_name: '',
        contact_name: '',
        business_email: '',
        phone: '',
        website: '',
        company_size: '',
        industry: '',
        message: '',
      });
      setIndustryOption('');
      setIndustryOther('');
    } catch (err) {
      setContactError(err.message || 'Failed to send enquiry');
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <section className="privacy-cta-section">
      <div className="privacy-cta-container container">
        <p className="privacy-cta-eyebrow">READY WHEN YOU ARE</p>
        <h2 className="privacy-cta-title">Plug in for the Asuma Experience.</h2>
        <p className="privacy-cta-subtitle">
          Get started in only takes a few minutes. Understand your website users,<br />
          start free, today.
        </p>
        <p className="privacy-cta-label">Privacy & Compliance</p>
        <div className="privacy-badges">
          {PRIVACY_FEATURES.map((feature) => (
            <div className="privacy-badge" key={feature.title}>
              {feature.title}
            </div>
          ))}
        </div>
        <div className="privacy-cta-buttons">
          <button className="cta-btn cta-btn-primary" onClick={() => navigate('/signup')}>Start Free Trial</button>
          <button className="cta-btn cta-btn-secondary" onClick={openContactModal}>Book a Demo</button>
        </div>
      </div>

      {showContactModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            overflowY: 'auto',
            padding: '24px 0',
          }}
          onClick={closeContactModal}
        >
          <div
            style={{
              maxWidth: 520,
              width: '100%',
              margin: '0 16px',
              background: '#020617',
              borderRadius: 16,
              padding: 24,
              boxShadow: '0 24px 70px rgba(15,23,42,0.9)',
              border: '1px solid rgba(148,163,184,0.4)',
              color: '#e5e7eb',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <p className="eyebrow">Book a Demo</p>
                <h3 style={{ margin: 0 }}>Talk to sales</h3>
              </div>
              <button
                type="button"
                onClick={closeContactModal}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  fontSize: 18,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            <p style={{ marginBottom: 16, fontSize: 14, color: '#9ca3af' }}>
              Share a few details about your business and we will reach out with demo scheduling and pricing options.
            </p>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Company name*</label>
                  <input
                    type="text"
                    name="company_name"
                    value={contactForm.company_name}
                    onChange={handleChange}
                    required
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Contact name</label>
                  <input
                    type="text"
                    name="contact_name"
                    value={contactForm.contact_name}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Business email*</label>
                  <input
                    type="email"
                    name="business_email"
                    value={contactForm.business_email}
                    onChange={handleChange}
                    required
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={contactForm.phone}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Website</label>
                  <input
                    type="text"
                    name="website"
                    value={contactForm.website}
                    onChange={handleChange}
                    placeholder="example.com"
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Company size</label>
                  <select
                    name="company_size"
                    value={contactForm.company_size}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  >
                    {COMPANY_SIZE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Industry</label>
                  <select 
                    value={industryOption} 
                    onChange={handleIndustryChange}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  >
                    {INDUSTRY_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                  {industryOption === 'Other' && (
                    <input
                      type="text"
                      value={industryOther}
                      onChange={handleIndustryOtherChange}
                      placeholder="Please specify"
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb', marginTop: 8 }}
                    />
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Message</label>
                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Tell us about your needs..."
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb', resize: 'vertical' }}
                  />
                </div>
              </div>
              {contactError && (
                <p style={{ color: '#ef4444', fontSize: 13, marginTop: 12 }}>{contactError}</p>
              )}
              {contactSuccess && (
                <p style={{ color: '#22c55e', fontSize: 13, marginTop: 12 }}>Thank you! We'll be in touch soon.</p>
              )}
              <button
                type="submit"
                disabled={contactSubmitting}
                style={{
                  width: '100%',
                  marginTop: 16,
                  padding: '10px',
                  borderRadius: 8,
                  border: 'none',
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  color: '#ffffff',
                  cursor: contactSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                }}
              >
                {contactSubmitting ? 'Sending…' : 'Send to sales'}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

function PricingCard({ name, price, annualPrice, note, ctaLabel, features, onContactSales, planId, monthlyPrice, stripePriceId, localMonthlyPrice, localAnnualPrice }) {
  const navigate = useNavigate();
  const handleClick = () => {
    if (onContactSales) {
      onContactSales();
    } else {
      const planInfo = {
        planId: planId,
        planName: name,
        monthlyPrice: monthlyPrice,
        stripePriceId: stripePriceId,
        features: features
      };
      localStorage.setItem('selectedPlanFromLanding', JSON.stringify(planInfo));
      navigate('/signup');
    }
  };
  return (
    <div className="pricing-card">
      <h3>{name}</h3>
      <div style={{ position: 'relative' }}>
        <p className="pricing-price">{localMonthlyPrice ? `${localMonthlyPrice}/month` : price}</p>
        {annualPrice && (
          <p style={{ fontSize: '14px', color: '#94a3b8', marginTop: '4px' }}>{annualPrice}</p>
        )}
        {annualPrice && name !== 'White Label' && (
          <div style={{
            position: 'absolute',
            top: '-10px',
            right: '-10px',
            background: '#10b981',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '600'
          }}>
            Save 15%
          </div>
        )}
      </div>
      {note ? <p className="pricing-note">{note}</p> : null}
      <ul className="pricing-features">
        {features.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      <button className="btn btn-primary pricing-cta" type="button" onClick={handleClick}>
        {ctaLabel || 'Sign Up'}
      </button>
    </div>
  );
}

function Pricing() {
  const { formatLocal } = useCurrencyDisplay();
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactError, setContactError] = useState('');
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactForm, setContactForm] = useState({
    company_name: '',
    contact_name: '',
    business_email: '',
    phone: '',
    website: '',
    company_size: '',
    industry: '',
    message: '',
  });
  const [industryOption, setIndustryOption] = useState('');
  const [industryOther, setIndustryOther] = useState('');

  const openContactModal = () => {
    setShowContactModal(true);
    setContactError('');
    setContactSuccess(false);
  };

  const closeContactModal = () => {
    if (contactSubmitting) return;
    setShowContactModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleIndustryChange = (e) => {
    const value = e.target.value;
    setIndustryOption(value);
    if (value === 'Other') {
      setIndustryOther('');
      setContactForm((prev) => ({ ...prev, industry: '' }));
    } else {
      setIndustryOther('');
      setContactForm((prev) => ({ ...prev, industry: value }));
    }
  };

  const handleIndustryOtherChange = (e) => {
    const value = e.target.value;
    setIndustryOther(value);
    setContactForm((prev) => ({ ...prev, industry: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.company_name || !contactForm.business_email) {
      setContactError('Please provide your company name and business email.');
      return;
    }
    setContactSubmitting(true);
    setContactError('');
    setContactSuccess(false);
    try {
      const rawWebsite = (contactForm.website || '').trim();
      const normalizedWebsite = rawWebsite
        ? `https://${rawWebsite.replace(/^https?:\/\//i, '')}`
        : '';
      const res = await fetch(`${API_BASE}/sales/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...contactForm,
          website: normalizedWebsite,
          plan: 'White Label',
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error((data && data.error) || 'Failed to send enquiry');
      }
      setContactSuccess(true);
      setContactForm({
        company_name: '',
        contact_name: '',
        business_email: '',
        phone: '',
        website: '',
        company_size: '',
        industry: '',
        message: '',
      });
      setIndustryOption('');
      setIndustryOther('');
    } catch (err) {
      setContactError(err.message || 'Failed to send enquiry');
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <section className="pricing container" id="pricing">
      <div className="section-head">
        <p className="eyebrow">Simple pricing</p>
        <h2 className="section-title" style={{ whiteSpace: 'nowrap' }}>Simple and transparent monthly plans</h2>
      </div>
      <div className="pricing-grid">
        {PRICING_PLANS.map((plan) => (
          <PricingCard
            key={plan.name}
            {...plan}
            onContactSales={plan.name === 'White Label' ? openContactModal : undefined}
            localMonthlyPrice={plan.monthlyPrice ? formatLocal(plan.monthlyPrice) : null}
          />
        ))}
      </div>

      {showContactModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            overflowY: 'auto',
            padding: '24px 0',
          }}
          onClick={closeContactModal}
        >
          <div
            style={{
              maxWidth: 520,
              width: '100%',
              margin: '0 16px',
              background: '#020617',
              borderRadius: 16,
              padding: 24,
              boxShadow: '0 24px 70px rgba(15,23,42,0.9)',
              border: '1px solid rgba(148,163,184,0.4)',
              color: '#e5e7eb',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <p className="eyebrow">White Label</p>
                <h3 style={{ margin: 0 }}>Talk to sales</h3>
              </div>
              <button
                type="button"
                onClick={closeContactModal}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  fontSize: 18,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            <p style={{ marginBottom: 16, fontSize: 14, color: '#9ca3af' }}>
              Share a few details about your business and we will reach out with White Label pricing and rollout options.
            </p>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Company name*</label>
                  <input
                    type="text"
                    name="company_name"
                    value={contactForm.company_name}
                    onChange={handleChange}
                    required
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Contact name</label>
                  <input
                    type="text"
                    name="contact_name"
                    value={contactForm.contact_name}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Business email*</label>
                  <input
                    type="email"
                    name="business_email"
                    value={contactForm.business_email}
                    onChange={handleChange}
                    required
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={contactForm.phone}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Website</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span
                      style={{
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: '1px solid #4b5563',
                        background: '#020617',
                        color: '#9ca3af',
                        fontSize: 13,
                      }}
                    >
                      https://
                    </span>
                    <input
                      type="text"
                      name="website"
                      value={contactForm.website}
                      onChange={handleChange}
                      placeholder="yourdomain.com"
                      style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                    />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Company size</label>
                  <select
                    name="company_size"
                    value={contactForm.company_size}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  >
                    {COMPANY_SIZE_OPTIONS.map((opt) => (
                      <option key={opt || 'empty'} value={opt}>
                        {opt || 'Select company size'}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Industry</label>
                  <select
                    name="industry_option"
                    value={industryOption}
                    onChange={handleIndustryChange}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                  >
                    {INDUSTRY_OPTIONS.map((opt) => (
                      <option key={opt || 'empty'} value={opt}>
                        {opt || 'Select industry'}
                      </option>
                    ))}
                  </select>
                  {industryOption === 'Other' && (
                    <input
                      type="text"
                      name="industry_other"
                      value={industryOther}
                      onChange={handleIndustryOtherChange}
                      placeholder="Please specify your industry"
                      style={{ marginTop: 8, width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb' }}
                    />
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Anything else we should know?</label>
                  <textarea
                    name="message"
                    rows={4}
                    value={contactForm.message}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #4b5563', background: '#020617', color: '#e5e7eb', resize: 'vertical' }}
                  />
                </div>
              </div>
              {contactError && (
                <p style={{ marginTop: 10, fontSize: 13, color: '#f97373' }}>{contactError}</p>
              )}
              {contactSuccess && (
                <p style={{ marginTop: 10, fontSize: 13, color: '#4ade80' }}>
                  Thank you. Your request has been sent to our sales team.
                </p>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                <button
                  type="button"
                  onClick={closeContactModal}
                  disabled={contactSubmitting}
                  className="btn btn-ghost"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={contactSubmitting}
                  className="btn btn-primary"
                >
                  {contactSubmitting ? 'Sending…' : 'Send to sales'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

function Footer() {
  const navigate = useNavigate();
  
  return (
    <footer className="ftr" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.8) 0%, rgba(139,92,246,0.7) 50%, rgba(168,85,247,0.6) 100%), rgba(4,7,18,0.1)' }}>
      <div className="container">
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '2fr 1fr 1fr 1fr', 
          gap: '40px', 
          alignItems: 'start',
          padding: '40px 0'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <img src="/headerlogo.png" alt="Asuma Logo" style={{ height: '28px', width: 'auto' }} />
              <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#000000', margin: 0 }}>Asuma</h3>
            </div>
            <p style={{ color: '#000000', fontSize: '14px', marginTop: '8px', marginBottom: '16px' }}>Customer intelligence for growing businesses.</p>
            <p className="muted">© {new Date().getFullYear()} Asuma, Inc. All rights reserved.</p>
          </div>
          <div className="ftr-links">
            <h4 style={{ fontSize: '14px', marginBottom: '8px', color: '#000000', fontWeight: '600' }}>Company</h4>
            <div style={{ width: '100%', height: '1px', background: 'rgba(255, 255, 255, 0.2)', marginBottom: '12px' }}></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span type="button" onClick={() => navigate('/about')} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px' }}>About</span>
              <span type="button" onClick={() => window.open('/privacy-policy.html', '_blank')} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px' }}>Privacy Policy</span>
              <span type="button" onClick={() => window.open('/terms-and-conditions.html', '_blank')} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px' }}>Terms & Conditions</span>
              <span type="button" onClick={() => window.open('/website-terms-of-use.html', '_blank')} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px' }}>Website Terms of Use</span>
              <span type="button" onClick={() => window.dispatchEvent(new CustomEvent('open-contact-modal'))} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px' }}>White Label</span>
              <span type="button" onClick={() => alert('Blog coming soon')} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px' }}>Blog</span>
            </div>
          </div>
          <div className="ftr-links">
            <h4 style={{ fontSize: '14px', marginBottom: '8px', color: '#000000', fontWeight: '600' }}>Contact</h4>
            <div style={{ width: '100%', height: '1px', background: 'rgba(255, 255, 255, 0.2)', marginBottom: '12px' }}></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#000000', opacity: 0.6, marginBottom: '2px' }}>General Enquiries</div>
                <span type="button" onClick={() => window.location.href = 'mailto:info@asumahq.com'} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px' }}>info@asumahq.com</span>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#000000', opacity: 0.6, marginBottom: '2px' }}>Support / Technical</div>
                <span type="button" onClick={() => window.location.href = 'mailto:support@asumahq.com'} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px' }}>support@asumahq.com</span>
              </div>
            </div>
          </div>
          <div className="ftr-links">
            <h4 style={{ fontSize: '14px', marginBottom: '8px', color: '#000000', fontWeight: '600' }}>Follow Us</h4>
            <div style={{ width: '100%', height: '1px', background: 'rgba(255, 255, 255, 0.2)', marginBottom: '12px' }}></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span type="button" onClick={() => window.open('https://www.linkedin.com/company/asumahq/', '_blank')} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <LinkedinOutlined style={{ fontSize: '16px', color: '#0077B5' }} />
                @asumahq
              </span>
              <span type="button" onClick={() => window.open('https://x.com/AsumaHQ', '_blank')} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <XOutlined style={{ fontSize: '16px', color: '#000000' }} />
                @AsumaHQ
              </span>
              <span type="button" onClick={() => window.open('https://www.youtube.com/@AsumaHQ', '_blank')} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <YoutubeOutlined style={{ fontSize: '16px', color: '#FF0000' }} />
                @AsumaHQ
              </span>
              <span type="button" onClick={() => window.open('https://instagram.com/asumahq', '_blank')} style={{ cursor: 'pointer', color: '#000000', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <InstagramOutlined style={{ fontSize: '16px', color: '#E4405F' }} />
                @asumahq
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  React.useEffect(() => {
    document.body.classList.add('light-mode');
  }, []);

  return (
    <>
      <Header />
      <Hero />
      <VisualGallery />
      <Metrics />
      <Features />
      <Integrations />
      <Pricing />
      <PrivacyCompliance />
      <Footer />
    </>
  );
}
